import {
  addDays,
  addWeeks,
  endOfDay,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isToday,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subWeeks,
} from 'date-fns';

export const WEEK_HOURS = Array.from({ length: 24 }, (_, i) => i);
export const HOUR_ROW_HEIGHT = 56;

export type WeekDayCell = {
  date: Date;
  dayLabel: string;
  dateLabel: string;
  isToday: boolean;
};

export type MiniMonthCell = {
  date: Date;
  inCurrentMonth: boolean;
  isToday: boolean;
  isInActiveWeek: boolean;
};

export function getActiveWeek(anchor: Date): WeekDayCell[] {
  const start = startOfWeek(anchor, { weekStartsOn: 0 });
  return Array.from({ length: 7 }, (_, i) => {
    const date = addDays(start, i);
    return {
      date,
      dayLabel: format(date, 'EEE').toUpperCase(),
      dateLabel: format(date, 'd'),
      isToday: isToday(date),
    };
  });
}

export type ViewKind = 'day' | '2day' | '4day' | 'week' | 'month' | 'year';

export function getActiveDays(anchor: Date, view: ViewKind): WeekDayCell[] {
  if (view === 'year') return [];

  if (view === 'day') {
    return [
      {
        date: anchor,
        dayLabel: format(anchor, 'EEE').toUpperCase(),
        dateLabel: format(anchor, 'd'),
        isToday: isToday(anchor),
      },
    ];
  }

  if (view === '2day' || view === '4day') {
    const len = view === '2day' ? 2 : 4;
    return Array.from({ length: len }, (_, i) => {
      const date = addDays(anchor, i);
      return {
        date,
        dayLabel: format(date, 'EEE').toUpperCase(),
        dateLabel: format(date, 'd'),
        isToday: isToday(date),
      };
    });
  }

  // For week view, align to the week start (Sunday) like Google Calendar.
  const start = startOfWeek(anchor, { weekStartsOn: 0 });
  const len = 7;
  return Array.from({ length: len }, (_, i) => {
    const date = addDays(start, i);
    return {
      date,
      dayLabel: format(date, 'EEE').toUpperCase(),
      dateLabel: format(date, 'd'),
      isToday: isToday(date),
    };
  });
}

export function getViewRange(activeDays: WeekDayCell[]) {
  const start = startOfDay(activeDays[0].date);
  const end = endOfDay(activeDays[activeDays.length - 1].date);
  return { start, end };
}

export function getWeekRange(anchor: Date) {
  const start = startOfDay(startOfWeek(anchor, { weekStartsOn: 0 }));
  const end = endOfDay(endOfWeek(anchor, { weekStartsOn: 0 }));
  return { start, end };
}

export function nextWeek(anchor: Date) {
  return addWeeks(anchor, 1);
}

export function previousWeek(anchor: Date) {
  return subWeeks(anchor, 1);
}

export function minutesFromMidnight(iso: string): number {
  const d = parseISO(iso);
  return d.getHours() * 60 + d.getMinutes();
}

export function timeLabel(iso: string): string {
  return format(parseISO(iso), 'p');
}

export function getMiniMonth(anchor: Date): MiniMonthCell[] {
  const monthStart = startOfMonth(anchor);
  const gridStart = startOfWeek(monthStart, { weekStartsOn: 0 });
  const weekDates = getActiveWeek(anchor).map((d) => d.date);

  return Array.from({ length: 42 }, (_, i) => {
    const date = addDays(gridStart, i);
    return {
      date,
      inCurrentMonth: isSameMonth(date, anchor),
      isToday: isToday(date),
      isInActiveWeek: weekDates.some((x) => isSameDay(x, date)),
    };
  });
}
